import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { projectsAPI, chatAPI, datasetsAPI, artifactsAPI, API_BASE } from '@/lib/api';
import { useWorkspaceStore, useRightPanelStore } from '@/lib/stores';
import { ChatComposer } from '@/components/chat/ChatComposer';
import { ChatMessages } from '@/components/chat/chat-messages';
import { Shimmer } from '@/components/ai-elements/shimmer';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Beaker, Upload, MessageSquare, Database as DatabaseIcon, BarChart3, FileText, ClipboardList, Settings, Loader2, CheckCircle, Circle, Play, Pause, Shield, FileStack, PenSquare } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import DataExplorerComponent from '@/components/data/DataExplorer';
import AuditTrailView from '@/components/audit/AuditTrailView';
import ModelDashboard from '@/components/modeling/ModelDashboard';
import EmptyChatState from '@/components/chat/EmptyChatState';

const projectSidebarNav = [
  { id: 'chat', icon: MessageSquare, label: 'Chat' },
  { id: 'data', icon: DatabaseIcon, label: 'Data' },
  { id: 'models', icon: BarChart3, label: 'Models' },
  { id: 'report', icon: FileText, label: 'Report' },
  { id: 'audit', icon: ClipboardList, label: 'Audit' },
  { id: 'config', icon: Settings, label: 'Config' },
];

export default function ProjectWorkspace() {
  const { id: projectId } = useParams();
  const {
    currentProject, setProject, currentSession, setSession,
    messages, setMessages, addMessage, artifacts, setArtifacts, addArtifact,
    isLoading, setLoading, currentDataset, setDataset
  } = useWorkspaceStore();
  const { open: openPanel } = useRightPanelStore();
  const [activeTab, setActiveTab] = useState('chat');
  const [pageLoading, setPageLoading] = useState(true);
  const [chatLoading, setChatLoading] = useState(false);
  const [templateProgress, setTemplateProgress] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadProject();
    return () => {
      setProject(null);
      setSession(null);
      setMessages([]);
      setArtifacts([]);
    };
  }, [projectId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadProject = async () => {
    try {
      const res = await projectsAPI.get(projectId);
      const project = res.data;
      setProject(project);

      // Load first session
      if (project.sessions?.length > 0) {
        const session = project.sessions[0];
        setSession(session);

        // Load chat history
        const histRes = await chatAPI.getHistory(session.id);
        setMessages(histRes.data.messages || []);

        // Load artifacts
        const artRes = await artifactsAPI.listBySession(session.id);
        setArtifacts(artRes.data.artifacts || []);
      }

      // Load datasets
      if (project.datasets?.length > 0) {
        setDataset(project.datasets[0]);
      }
    } catch (e) {
      console.error('Failed to load project:', e);
      toast.error('Failed to load project');
    } finally {
      setPageLoading(false);
    }
  };

  const handleSendMessage = async (text, overrideDatasetId = null) => {
    if (!currentSession) return;
    setChatLoading(true);

    // Optimistic add user message
    const userMsg = {
      id: Date.now().toString(),
      session_id: currentSession.id,
      role: 'user',
      content: text,
      widgets: [],
      timestamp: new Date().toISOString(),
    };
    addMessage(userMsg);

    try {
      // Use SSE streaming for real-time response
      const streamUrl = `${API_BASE}/chat/stream`;
      const response = await fetch(streamUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('pharmax_token') || ''}`,
        },
        body: JSON.stringify({
          message: text,
          session_id: currentSession.id,
          dataset_id: overrideDatasetId || currentDataset?.id || null,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      const responseId = Date.now().toString() + '-response';
      let assistantMsg = {
        id: responseId,
        role: 'assistant',
        content: '',
        widgets: [],
        agent: 'pharmagent',
        suggested_actions: [],
        timestamp: new Date().toISOString(),
      };
      let messageAdded = false;
      let sseBuffer = '';

      const processEvent = (event) => {
        if (event.type === 'thinking' || event.type === 'metadata') {
          assistantMsg.agent = event.agent || assistantMsg.agent;
          if (!messageAdded) {
            addMessage({ ...assistantMsg });
            messageAdded = true;
          }
        } else if (event.type === 'reasoning') {
          assistantMsg.reasoning = (assistantMsg.reasoning || '') + (event.content || '');
          if (!messageAdded) { addMessage({ ...assistantMsg }); messageAdded = true; }
          const { messages } = useWorkspaceStore.getState();
          setMessages(messages.map(m => m.id === assistantMsg.id ? { ...assistantMsg } : m));
        } else if (event.type === 'tool_call') {
          assistantMsg.tool_calls = [...(assistantMsg.tool_calls || []), event.tool];
          if (!messageAdded) { addMessage({ ...assistantMsg }); messageAdded = true; }
          const { messages } = useWorkspaceStore.getState();
          setMessages(messages.map(m => m.id === assistantMsg.id ? { ...assistantMsg } : m));
        } else if (event.type === 'tool_result') {
          const calls = assistantMsg.tool_calls || [];
          const idx = calls.findIndex(t => t.id === event.tool_id);
          if (idx >= 0) {
            calls[idx] = { ...calls[idx], output: event.output, status: event.status || 'complete' };
            assistantMsg.tool_calls = [...calls];
            const { messages } = useWorkspaceStore.getState();
            setMessages(messages.map(m => m.id === assistantMsg.id ? { ...assistantMsg } : m));
          }
        } else if (event.type === 'plan') {
          assistantMsg.plan = event.plan;
          if (!messageAdded) { addMessage({ ...assistantMsg }); messageAdded = true; }
          const { messages } = useWorkspaceStore.getState();
          setMessages(messages.map(m => m.id === assistantMsg.id ? { ...assistantMsg } : m));
        } else if (event.type === 'chain_of_thought') {
          assistantMsg.chain_of_thought = [...(assistantMsg.chain_of_thought || []), event.step];
          if (!messageAdded) { addMessage({ ...assistantMsg }); messageAdded = true; }
          const { messages } = useWorkspaceStore.getState();
          setMessages(messages.map(m => m.id === assistantMsg.id ? { ...assistantMsg } : m));
        } else if (event.type === 'sources') {
          assistantMsg.sources = event.sources;
          const { messages } = useWorkspaceStore.getState();
          setMessages(messages.map(m => m.id === assistantMsg.id ? { ...assistantMsg } : m));
        } else if (event.type === 'confirmation') {
          assistantMsg.confirmation = event.confirmation;
          if (!messageAdded) { addMessage({ ...assistantMsg }); messageAdded = true; }
          const { messages } = useWorkspaceStore.getState();
          setMessages(messages.map(m => m.id === assistantMsg.id ? { ...assistantMsg } : m));
        } else if (event.type === 'widget') {
          assistantMsg.widgets = [...assistantMsg.widgets, event.widget];
          const { messages } = useWorkspaceStore.getState();
          const updated = messages.map(m => m.id === assistantMsg.id ? { ...assistantMsg } : m);
          setMessages(updated);

          if (event.widget.type !== 'text' && event.widget.type !== 'error') {
            addArtifact({
              id: event.widget.id, name: event.widget.title || event.widget.type,
              type: event.widget.type, agent: event.widget.agent,
            });
          }
        } else if (event.type === 'actions') {
          assistantMsg.suggested_actions = event.actions || [];
          const { messages } = useWorkspaceStore.getState();
          const updated = messages.map(m => m.id === assistantMsg.id ? { ...assistantMsg } : m);
          setMessages(updated);
        } else if (event.type === 'done') {
          assistantMsg.id = event.message_id || assistantMsg.id;
          const { messages } = useWorkspaceStore.getState();
          const updated = messages.map(m =>
            (m.id === assistantMsg.id || m.id === responseId)
              ? { ...assistantMsg } : m
          );
          setMessages(updated);
        } else if (event.type === 'error') {
          assistantMsg.widgets = [...assistantMsg.widgets, {
            id: Date.now().toString(), type: 'error', title: 'Error', content: event.message,
          }];
          if (!messageAdded) { addMessage({ ...assistantMsg }); messageAdded = true; }
          else {
            const { messages } = useWorkspaceStore.getState();
            setMessages(messages.map(m => m.id === assistantMsg.id ? { ...assistantMsg } : m));
          }
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        sseBuffer += decoder.decode(value, { stream: true });
        const parts = sseBuffer.split('\n\n');
        sseBuffer = parts.pop() || '';

        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            processEvent(event);
          } catch (parseErr) {
            // skip malformed
          }
        }
      }

      // Process any remaining buffer
      if (sseBuffer.trim().startsWith('data: ')) {
        try {
          const event = JSON.parse(sseBuffer.trim().slice(6));
          processEvent(event);
        } catch (e) {}
      }

      // Final update if not yet added
      if (!messageAdded) {
        addMessage(assistantMsg);
      }
    } catch (e) {
      console.error('Chat error:', e);
      addMessage({
        id: Date.now().toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        widgets: [],
        agent: 'system',
        timestamp: new Date().toISOString(),
      });
    } finally {
      setChatLoading(false);
    }
  };

  const handleFileUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('project_id', projectId);

    try {
      toast.info('Uploading dataset...');
      const res = await datasetsAPI.upload(formData);
      const dataset = res.data;
      setDataset(dataset);
      toast.success(`Dataset uploaded: ${dataset.filename} (${dataset.n_observations} rows)`);

      // Auto-profile
      if (currentSession) {
        handleSendMessage(`Profile the uploaded dataset ${dataset.filename}`);
      }
    } catch (err) {
      toast.error('Failed to upload dataset: ' + (err.response?.data?.detail || err.message));
      console.error(err);
    }

    e.target.value = '';
  };

  const handleSuggestedAction = (action) => {
    if (!chatLoading) {
      handleSendMessage(action);
    }
  };

  const handleAddToReport = (text, agent, blockType = 'text', title = null, widget = null) => {
    const { addReportBlock } = useWorkspaceStore.getState();
    const block = {
      id: Date.now().toString(),
      type: blockType || 'text',
      title: title || (agent ? `${agent} output` : 'Chat section'),
      content: text,
      agent: agent || 'unknown',
      widget_data: widget ? { type: widget.type, data: widget.data, title: widget.title } : null,
      order: (useWorkspaceStore.getState().reportBlocks || []).length,
    };
    addReportBlock(block);
    openPanel('report');
    toast.success(`${blockType === 'plot' ? 'Figure' : blockType === 'table' ? 'Table' : 'Section'} added to Report Builder`);
  };

  const handleLaunchTemplate = async (templateKey) => {
    if (!templateKey) {
      // No key = browse templates page
      window.location.href = '/app/templates';
      return;
    }
    if (!currentSession) return;
    try {
      toast.info('Starting workflow template...');
      const res = await axios.post(`${API_BASE}/workflow-templates/execute`, {
        template_key: templateKey,
        session_id: currentSession.id,
      });
      setTemplateProgress(res.data.progress);
      toast.success(`Template "${res.data.template.name}" initialized`);
      
      // Auto-execute first step
      await executeTemplateStep();
    } catch (err) {
      toast.error('Failed to launch template: ' + (err.response?.data?.detail || err.message));
    }
  };

  const executeTemplateStep = async (action = 'execute') => {
    if (!currentSession) return;
    setChatLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/workflow-templates/step`, {
        session_id: currentSession.id,
        action,
      });
      
      if (res.data.message) {
        addMessage(res.data.message);
      }
      setTemplateProgress(res.data.progress);
      
      if (res.data.status === 'complete') {
        toast.success('Workflow template completed!');
        setTemplateProgress(null);
      } else if (res.data.status === 'awaiting_review') {
        toast.info('Human review required before continuing');
      } else if (res.data.status === 'step_complete') {
        // Auto-execute next step after a small delay
        setTimeout(() => executeTemplateStep(), 500);
      }
    } catch (err) {
      toast.error('Template step failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setChatLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="flex flex-col md:flex-row h-[calc(100vh-3.5rem)]">
        <div className="flex md:flex-col md:w-14 border-b md:border-b-0 md:border-r border-border p-2 gap-2 overflow-x-auto">
          {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-10 w-10 min-w-[40px] rounded-lg flex-shrink-0" />)}
        </div>
        <div className="flex-1 p-4 sm:p-8">
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-4 w-48 mb-8" />
          <div className="space-y-4">
            {[1,2,3].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row h-[calc(100vh-3.5rem)]">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.tsv,.xpt,.xlsx"
        onChange={handleFileSelected}
        className="hidden"
        data-testid="dataset-upload-file-input"
      />

      {/* Project sidebar — vertical on desktop, horizontal scroll on mobile */}
      <div className="md:w-14 border-b md:border-b-0 md:border-r border-border flex md:flex-col items-center py-1.5 md:py-3 gap-1 flex-shrink-0 overflow-x-auto md:overflow-x-visible px-2 md:px-0">
        {projectSidebarNav.map((item) => (
          <Button
            key={item.id}
            variant="ghost"
            size="icon"
            onClick={() => setActiveTab(item.id)}
            data-testid={`project-nav-${item.id}`}
            className={`h-10 w-10 min-w-[40px] rounded-lg flex-shrink-0 ${
              activeTab === item.id
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            <item.icon className="w-[18px] h-[18px]" />
          </Button>
        ))}
        {/* Mobile: panel buttons that are hidden from topbar on small screens */}
        <div className="flex items-center gap-1 md:hidden ml-auto">
          <Button variant="ghost" size="icon" onClick={() => openPanel('artifacts')}
            data-testid="mobile-artifacts-button"
            className={`h-10 w-10 min-w-[40px] rounded-lg flex-shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted/50`}>
            <FileStack className="w-[18px] h-[18px]" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => openPanel('report')}
            data-testid="mobile-report-button"
            className={`h-10 w-10 min-w-[40px] rounded-lg flex-shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted/50`}>
            <PenSquare className="w-[18px] h-[18px]" />
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 relative"
        onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); if (e.currentTarget === e.target) setIsDragging(false); }}
        onDrop={(e) => {
          e.preventDefault(); setIsDragging(false);
          const file = e.dataTransfer?.files?.[0];
          if (file && (file.name.endsWith('.csv') || file.name.endsWith('.xpt') || file.name.endsWith('.xlsx') || file.name.endsWith('.tsv'))) {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('project_id', projectId);
            toast.info('Uploading ' + file.name + '...');
            datasetsAPI.upload(formData).then(res => {
              setDataset(res.data);
              toast.success(`Uploaded: ${res.data.filename} (${res.data.n_observations} rows)`);
              if (currentSession) handleSendMessage(`Profile the uploaded dataset ${res.data.filename}`);
            }).catch(err => toast.error('Upload failed'));
          }
        }}
      >
        {/* Drag overlay */}
        {isDragging && (
          <div className="absolute inset-0 z-30 bg-primary/10 border-2 border-dashed border-primary rounded-xl flex items-center justify-center">
            <div className="text-center">
              <Upload className="w-10 h-10 text-primary mx-auto mb-2" />
              <p className="text-sm font-medium text-primary">Drop dataset to upload</p>
              <p className="text-xs text-primary/60">CSV, XPT, SAS7BDAT, XLSX</p>
            </div>
          </div>
        )}
        
        {activeTab === 'chat' ? (
          <>
            {/* Chat messages */}
            <div className="flex-1 overflow-y-auto">
              {messages.length === 0 && !chatLoading ? (
                <EmptyChatState
                  dataset={currentDataset}
                  projectId={projectId}
                  onSendMessage={handleSendMessage}
                  onUploadFile={handleFileUpload}
                  onDatasetLoaded={(ds) => {
                    setDataset(ds);
                  }}
                  onLaunchTemplate={(key) => {
                    if (key) {
                      handleLaunchTemplate(key);
                    } else {
                      window.location.href = '/app/templates';
                    }
                  }}
                />
              ) : (
                <div className="pb-4">
                  <ChatMessages
                    messages={messages}
                    isLoading={chatLoading}
                    onSuggestionClick={handleSuggestedAction}
                    onAddToReport={handleAddToReport}
                    sessionId={currentSession?.id}
                  />
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Template progress bar */}
            {templateProgress && (
              <div className="px-3 sm:px-4 py-2 border-t border-border/50 bg-muted/20">
                <div className="max-w-[42rem] mx-auto">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium" style={{ fontFamily: 'Space Grotesk' }}>
                      Workflow Progress
                    </span>
                    <span className="text-[10px] font-mono text-muted-foreground">
                      Step {templateProgress.current_step + 1} of {templateProgress.total_steps}
                    </span>
                  </div>
                  <Progress value={templateProgress.pct} className="h-1.5 mb-2" />
                  <div className="flex gap-1.5 flex-wrap">
                    {templateProgress.steps?.map((step, i) => (
                      <span
                        key={i}
                        className={`text-[9px] px-1.5 py-0.5 rounded-full font-mono ${
                          step.status === 'complete' ? 'bg-green-500/15 text-green-500' :
                          step.status === 'running' ? 'bg-primary/15 text-primary' :
                          step.status === 'failed' ? 'bg-destructive/15 text-destructive' :
                          'bg-muted text-muted-foreground'
                        }`}
                      >
                        {step.status === 'complete' ? '✓' : step.status === 'running' ? '⟳' : '○'} {step.agent}
                      </span>
                    ))}
                  </div>
                  {templateProgress.steps?.some(s => s.status === 'running' && s.human_review) && (
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" className="rounded-full h-7 text-xs px-3" onClick={() => executeTemplateStep('approve')}>
                        <CheckCircle className="w-3 h-3 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full h-7 text-xs px-3" onClick={() => executeTemplateStep('skip')}>
                        Skip Step
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Composer */}
            <ChatComposer
              onSend={handleSendMessage}
              onFileUpload={handleFileUpload}
              onLaunchTemplate={handleLaunchTemplate}
              isLoading={chatLoading}
              disabled={!currentSession}
              streamingAgent={chatLoading ? (messages[messages.length - 1]?.agent || 'PharmAgent') : null}
            />
          </>
        ) : activeTab === 'data' ? (
          <DataExplorerComponent projectId={projectId} onUploadComplete={(ds) => setDataset(ds)} />
        ) : activeTab === 'models' ? (
          <ModelDashboard sessionId={currentSession?.id} />
        ) : activeTab === 'audit' ? (
          <AuditTrailView sessionId={currentSession?.id} />
        ) : (
          <PlaceholderTab tab={activeTab} />
        )}
      </div>
    </div>
  );
}

function PlaceholderTab({ tab }) {
  const labels = {
    models: { title: 'Model Development', desc: 'Track and manage your modeling runs' },
    report: { title: 'Report', desc: 'Generate regulatory-ready reports from analysis artifacts' },
    audit: { title: 'Audit Trail', desc: 'View complete log of all analysis actions' },
    config: { title: 'Configuration', desc: 'Project settings and configuration' },
  };
  const info = labels[tab] || { title: tab, desc: '' };
  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-6">
      <h2 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{info.title}</h2>
      <p className="text-sm text-muted-foreground mt-1">{info.desc}</p>
      <p className="text-xs text-muted-foreground/50 mt-4">Coming soon in next update</p>
    </div>
  );
}
